import { isEmptyArray } from "formik"
import { isArray } from "lodash"

import { DownloadType } from "@api/schema"
import { AttachmentDefinitionType, IAttachmentDefinition, IFileAttachmentDefinition, IProjectPlanProposalAttachment, IProposal, IProposalAttachment, isFileAttachmentDefinition, ProposalAttachmentType } from "@api/schema/challenges"
import { idFromIModelOrIRI } from "@basics/util-importless"
import { PROPOSAL_ATTACHMENT_FILE_TYPES } from "@config/index"
import { EntityType } from "@redux/common/reduxTypes"
import { CreateableProposalAttachmentEntityType } from "@redux/usecases/proposalAttachments/schema-definitions"

import { listOfFileTypes as listOfFileTypesFromUtil } from "./util"

export class ProposalAttachmentHelper {


  /**
   * Filters a list of attachments to return only those that match a given attachment
   * definition and belong to the proposal.
   *
   * @param {IProposalAttachment} proposalAttachments - The proposal attachment to filter for.
   * @param {IAttachmentDefinition} attachmentDefinition - The attachment definition to match against.
   * @param {IProposal} proposal - The proposal to match againgst.
   * @returns {IProposalAttachment[]} An array of proposal attachments
   *
   * @example
   * const matching = filterProposalAttachmentsByDefinitionAndProposal(myDefinition);
   */
  public static filterProposalAttachmentsByDefinitionAndProposal = (
    proposalAttachments: IProposalAttachment[],
    attachmentDefinition: IAttachmentDefinition,
    proposal: IProposal
  ): IProposalAttachment[] =>
    proposalAttachments.filter(attachment =>
      idFromIModelOrIRI(attachment.definition) === attachmentDefinition.id &&
      idFromIModelOrIRI(attachment.proposal) === proposal.id
    )

  /**
   * Determines if the attachmentDefinition and the proposal has non read only proposal attachments.
   *
   * @param {IProposalAttachment} proposalAttachments - The proposal attachment to check.
   * @param {IAttachmentDefinition} attachmentDefinition - The attachment definition to check.
   * @param {IProposal} proposal - The proposal to check.
   * @returns true when the attachmentDefinition and the proposal has non read only proposal attachments.
   */
  public static hasNonReadOnlyAttachment = (
    proposalAttachments: IProposalAttachment[],
    attachmentDefinition: IAttachmentDefinition,
    proposal: IProposal
  ): boolean =>
    ProposalAttachmentHelper.filterProposalAttachmentsByDefinitionAndProposal(
      proposalAttachments,
      attachmentDefinition,
      proposal
    ).filter(attachment =>
      !attachment.readOnly
    ).length > 0

  /**
   * Determines whether the number of existing file proposal attachments for a given attachment definition
   * and the proposal has reached or exceeded the maximum allowed count.
   *
   * @param {IProposalAttachment} proposalAttachments - The proposal attachment to check.
   * @param {IAttachmentDefinition} attachmentDefinition - The attachment definition to check.
   * @param {IProposal} proposal - The proposal to check.
   * @returns {boolean} True if the number of matching attachments is greater than or equal
   * to the allowed maximum; otherwise false.
   *
   * @example
   * // If metadata.maxCount is 3 and there are already 3 matching attachments, this returns true.
   * const isFull = hasReachedFileAttachmentLimit(myAttachmentDefinition);
   */
  public static hasReachedFileAttachmentLimit = (
    proposalAttachments: IProposalAttachment[],
    attachmentDefinition: IFileAttachmentDefinition,
    proposal: IProposal
  ): boolean =>
    ProposalAttachmentHelper.filterProposalAttachmentsByDefinitionAndProposal(
      proposalAttachments,
      attachmentDefinition,
      proposal
    ).length >= attachmentDefinition.metadata.maxCount

  /**
   * Determines whether a proposal attachment can be created for a given attachment definition
   * and a proposal.
   *
   * @param {IProposalAttachment} proposalAttachments - The proposal attachment to check.
   * @param {IAttachmentDefinition} attachmentDefinition - The attachment definition to check.
   * @param {IProposal} proposal - The proposal to check.
   * @returns {boolean} True when the proposal attachment popup should be disabled for the given definition,
   * false otherwise.
   */
  public static proposalAttachmentCanBeCreatedFor = (
    proposalAttachments: IProposalAttachment[],
    attachmentDefinition: IAttachmentDefinition,
    proposal: IProposal
  ): boolean => {
    switch (attachmentDefinition.type) {
      case AttachmentDefinitionType.Document:
      case AttachmentDefinitionType.ProjectPlan:
        return !ProposalAttachmentHelper.hasNonReadOnlyAttachment(
          proposalAttachments,
          attachmentDefinition,
          proposal
        )
      case AttachmentDefinitionType.File:
        return isFileAttachmentDefinition(attachmentDefinition)
          && !ProposalAttachmentHelper.hasReachedFileAttachmentLimit(
            proposalAttachments,
            attachmentDefinition,
            proposal
          )
      default: attachmentDefinition.type satisfies never
    }
  }

  /**
   * Checks if the given project plan proposal attachment is auto generated based on the project data.
   *
   * @param projectPlanProposalAttachment
   * @returns true when project plan proposal attachment is auto generated
   */
  public static isProjectPlanAutoGenerated = (projectPlanProposalAttachment: IProjectPlanProposalAttachment): boolean =>
    projectPlanProposalAttachment.metadata
    && !isEmptyArray(projectPlanProposalAttachment.metadata)
    && !Array.isArray(projectPlanProposalAttachment.metadata)
    && Object.hasOwn(projectPlanProposalAttachment.metadata, "contentIds")
    && isArray(projectPlanProposalAttachment.metadata.contentIds)

  /**
   * Returns a comma-separated list of unique attachment file type keys that match
   * the MIME types declared on the provided attachment definition.
   *
   * @see using the function {@link listOfFileTypesFromUtil}
   *
   * @param {IAttachmentDefinition} aDef - Attachment definition containing metadata about the attachment.
   * @returns {string} Comma-separated string of unique file type keys (from PROPOSAL_ATTACHMENT_FILE_TYPES)
   * * that matched any of the MIME types in aDef.metadata.mimeTypes. Returns an empty string if no matches.
   */
  public static listOfFileTypes = (aDef: IAttachmentDefinition): string => {
    return listOfFileTypesFromUtil(aDef.metadata.mimeTypes, PROPOSAL_ATTACHMENT_FILE_TYPES)
  }

  /**
   * Get the correlated ProposalAttachmentType from the given AttachmentDefinitionType
   */
  public static getProposalAttachmentTypeFromAttachmentDefinitionType = (attachmentDefinitionType: AttachmentDefinitionType):
    ProposalAttachmentType | undefined => {
    switch (attachmentDefinitionType) {
      case AttachmentDefinitionType.Document:
        return "DocumentProposalAttachment"
      case AttachmentDefinitionType.File:
        return "FileProposalAttachment"
      case AttachmentDefinitionType.ProjectPlan:
        return "ProjectPlanProposalAttachment"
      default:
        return undefined
    }
  }

  /**
   * Map a ProposalAttachmentType to the corresponding {@link CreateableProposalAttachmentEntityType}.
   *
   * @param {ProposalAttachmentType} proposalAttachmentType - The kind of proposal attachment.
   * @returns {DownloadType} The corresponding Entity enum value.
   */
  public static getEntityTypeFromProposalAttachmentType = (proposalAttachmentType: ProposalAttachmentType):
    CreateableProposalAttachmentEntityType | undefined => {
    switch (proposalAttachmentType) {
      case "DocumentProposalAttachment":
        return EntityType.DocumentProposalAttachment
      case "FileProposalAttachment":
        return EntityType.FileProposalAttachment
      case "ProjectPlanProposalAttachment":
        return EntityType.ProjectPlanProposalAttachment
      default:
        return undefined
    }
  }

  /**
   * Map a ProposalAttachmentType to the corresponding DownloadType.
   *
   * @param {ProposalAttachmentType} proposalAttachmentType - The kind of proposal attachment.
   * @returns {DownloadType} The corresponding DownloadType enum value.
   */
  public static getDownloadTypeFromProposalAttachmentType = (proposalAttachmentType: ProposalAttachmentType):
    DownloadType => {
    switch (proposalAttachmentType) {
      case "DocumentProposalAttachment":
        return DownloadType.DocumentProposalAttachment
      case "FileProposalAttachment":
        return DownloadType.FileProposalAttachment
      case "ProjectPlanProposalAttachment":
        return DownloadType.ProjectPlanProposalAttachment
      default: proposalAttachmentType satisfies never
    }
  }
}